/**
 * REM Voice embed.js loader + typed wrappers.
 * Script URL comes from /api/rem-voice/session (or config); never hardcode secrets.
 */

export type RemVoiceMode = 'simple' | 'full';

export interface RemVoiceEvent {
  source?: string;
  type?: string;
  [key: string]: unknown;
}

export interface RemVoiceMountOptions {
  token: string;
  el: string | HTMLElement;
  mode?: RemVoiceMode;
  onEvent?: (ev: RemVoiceEvent) => void;
  width?: string;
  height?: string;
}

export interface RemVoiceDialOpts {
  mode?: RemVoiceMode;
  contactName?: string;
  leadId?: string;
  dialCode?: string;
  crmUserId?: string;
  agentId?: string;
  source?: string;
}

export interface RemVoiceApi {
  mount: (opts: RemVoiceMountOptions) => RemVoiceApi;
  dial: (number: string, opts?: RemVoiceDialOpts) => RemVoiceApi;
  hangup: () => void;
  answer: () => void;
  reject: () => void;
  mute: (muted: boolean) => void;
  setToken: (token: string) => void;
  setMode: (mode: RemVoiceMode) => RemVoiceApi;
  open?: () => void;
  close?: () => void;
}

declare global {
  interface Window {
    RemVoice?: RemVoiceApi;
  }
}

const DEFAULT_SCRIPT = 'https://voices.itempire.info/dialer/embed.js';

let loadPromise: Promise<RemVoiceApi> | null = null;

export function loadRemVoiceScript(scriptUrl?: string): Promise<RemVoiceApi> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('RemVoice is browser-only'));
  }
  if (window.RemVoice) {
    return Promise.resolve(window.RemVoice);
  }
  if (loadPromise) return loadPromise;

  const src = scriptUrl || DEFAULT_SCRIPT;
  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[data-rem-voice-embed="1"]`);
    if (existing && window.RemVoice) {
      resolve(window.RemVoice);
      return;
    }

    const script = existing || document.createElement('script');
    if (!existing) {
      script.src = src;
      script.async = true;
      script.dataset.remVoiceEmbed = '1';
      document.head.appendChild(script);
    }

    script.addEventListener('load', () => {
      if (window.RemVoice) resolve(window.RemVoice);
      else reject(new Error('RemVoice global missing after embed.js load'));
    });
    script.addEventListener('error', () => {
      loadPromise = null;
      reject(new Error('Failed to load REM Voice embed.js'));
    });
  });

  return loadPromise;
}

/** Normalize phone for dial: keep digits; preserve leading +; UAE 0xxxxxxxxx left as-is. */
export function normalizeDialNumber(raw: string): string {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';

  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';

  // +9715… → 05… (local format preferred by REM SIM trunks)
  if (digits.startsWith('971') && digits.length >= 12) {
    return `0${digits.slice(3)}`;
  }
  // 9715… without +
  if (!hasPlus && digits.startsWith('971') && digits.length >= 12) {
    return `0${digits.slice(3)}`;
  }

  return hasPlus && !digits.startsWith('971') ? `+${digits}` : digits;
}

/** Common 1- and 2-digit ITU country calling codes (3-digit codes fall through). */
const TWO_DIGIT_DIAL_CODES = new Set([
  '20', '27', '30', '31', '32', '33', '34', '36', '39',
  '40', '41', '43', '44', '45', '46', '47', '48', '49',
  '51', '52', '53', '54', '55', '56', '57', '58',
  '60', '61', '62', '63', '64', '65', '66',
  '81', '82', '84', '86', '90', '91', '92', '93', '94', '95', '98',
]);

/**
 * Country calling code for click-to-dial metadata.
 * UAE 05… / 971… / +971… → "971". Other +CC numbers → digits after +. Local numbers without CC → undefined.
 */
export function extractDialCode(raw: string): string | undefined {
  const trimmed = String(raw || '').trim();
  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return undefined;

  if (digits.startsWith('971')) return '971';
  // UAE local mobile: 05xxxxxxxx (10+ digits starting with 05)
  if (digits.startsWith('05') && digits.length >= 10) return '971';

  if (!trimmed.startsWith('+')) return undefined;
  if (digits.startsWith('1')) return '1';
  const two = digits.slice(0, 2);
  if (TWO_DIGIT_DIAL_CODES.has(two)) return two;
  if (digits.length >= 3) return digits.slice(0, 3);
  return two || undefined;
}
